import React, { type FocusEvent, type KeyboardEvent, useState } from 'react';
import isNumeric from 'validator/es/lib/isNumeric';
import Input from '@components/Input';
import limitNum from '@utils/limitNum';

const round2 = (num: number) => Math.round(num * 100) / 100;
const limitCountValue = (val: string | number) => {
  return Math.round(limitNum({ min: 1, max: 999, value: Number(val) }));
};
const limitTimeValue = (val: string | number) => {
  return round2(limitNum({ min: 0.01, max: 999, value: Number(val) }));
};

interface Props {
  frame: number;
  duration: number;
  onChange: (params: { frame: number; duration: number }) => void;
}

const getTriggerProps = (cb: (val: string) => void) => {
  const onBlur = (e: FocusEvent<HTMLInputElement>) => {
    cb(e.target.value);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };
  return { onBlur, onKeyDown };
};

function FrameSetting({ duration, frame, onChange }: Props) {
  const [count, setCount] = useState<string | number>(frame);
  const [dur, setDur] = useState<string | number>(duration);
  const [time, setTime] = useState<string | number>(round2(duration * frame));

  const handleChange = (newCount: number, newDur: number) => {
    setCount(newCount);
    setDur(newDur);
    setTime(round2(newCount * newDur));
    onChange({ frame: newCount, duration: newDur });
  };

  const handleCountChange = (val: string) => {
    if (!isNumeric(val)) {
      setCount(frame);
      return;
    }
    const newCount = limitCountValue(val);
    const newDur = limitTimeValue(Number(time) / newCount);
    handleChange(newCount, newDur);
  };

  const handleDurChange = (val: string) => {
    if (!isNumeric(val)) {
      setDur(duration);
      return;
    }
    const countVal = Number(count);
    const newDur = limitTimeValue(val);
    const newTime = limitTimeValue(newDur * countVal);
    handleChange(countVal, round2(newTime / countVal));
  };

  const handleTimeChange = (val: string) => {
    if (!isNumeric(val)) {
      setTime(round2(duration * frame));
      return;
    }
    const countVal = Number(count);
    const newTime = limitTimeValue(val);
    const newDur = limitTimeValue(newTime / countVal);
    handleChange(countVal, newDur);
  };

  return (
    <>
      <div className="mt-20 flex items-center">
        <div>序列帧总帧数：</div>
        <Input
          scheme="dark"
          className="ml-auto mr-10 h-30 w-80"
          value={count}
          onChange={(e) => setCount(e.target.value)}
          {...getTriggerProps(handleCountChange)}
        />
        <div>帧</div>
      </div>
      <div className="mt-20 flex items-center">
        <div>总播放时长：</div>
        <Input
          scheme="dark"
          className="ml-auto mr-10 h-30 w-80"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          {...getTriggerProps(handleTimeChange)}
        />
        <div>秒</div>
      </div>
      <div className="mt-20 flex items-center">
        <div>单帧播放时长：</div>
        <Input
          scheme="dark"
          className="ml-auto mr-10 h-30 w-80"
          value={dur}
          onChange={(e) => setDur(e.target.value)}
          {...getTriggerProps(handleDurChange)}
        />
        <div>秒</div>
      </div>
    </>
  );
}

export default FrameSetting;
